import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { previewOperation, useApply, useUndoRedo } from "./api/queries";
import { ApiError } from "./api/client";
import type { CharacterView, ComputedValue, Operation, PreviewView, SystemInfo, TermLanguage, ValueChangeView } from "./api/types";
import { ChangeNotice } from "./components/ChangeNotice";
import { PreviewDialog } from "./components/PreviewDialog";
import { DerivationSheet } from "./components/DerivationSheet";
import { formatAmount } from "./text";

interface CharacterContextValue {
  character: CharacterView;
  system: SystemInfo | undefined;
  language: TermLanguage;
  value: (key: string) => ComputedValue | undefined;
  /** Formats an amount in the units of the character's rule set; a computed value with a currency is shown this way. */
  amount: (currency: string | null | undefined, value: number, signed?: boolean) => string;
  /** The display text of a computed value, in currency units when it has a currency. */
  display: (computed: ComputedValue | undefined) => string;
  /** Applies the operation immediately. */
  apply: (operation: Operation) => Promise<boolean>;
  /** Applies the operation immediately and then shows what it changed, with undo. */
  choose: (operation: Operation, title: string) => Promise<boolean>;
  /** Shows the before/after preview and applies only after confirmation; true once applied, false when cancelled. */
  confirm: (operation: Operation, title?: string) => Promise<boolean>;
  showDerivation: (key: string) => void;
  busy: boolean;
  error: ApiError | null;
  clearError: () => void;
}

const CharacterContext = createContext<CharacterContextValue | null>(null);

export function useCharacter(): CharacterContextValue {
  const context = useContext(CharacterContext);
  if (!context) throw new Error("useCharacter außerhalb eines Charakters");
  return context;
}

/** The character around a component that also appears outside one. */
export function useOptionalCharacter(): CharacterContextValue | null {
  return useContext(CharacterContext);
}

/**
 * The number of a computed value as it would be after an unsaved draft, e.g. in an editor that saves on demand. The engine computes it
 * from the preview of the operation; without a draft, and until the preview arrives, it is the saved value.
 */
export function useDraftValue(operation: Operation | null, key: string): number | null {
  const { character, value } = useCharacter();
  const saved = value(key)?.number ?? null;
  const signature = operation ? JSON.stringify(operation) : null;
  const [draft, setDraft] = useState<{ signature: string; number: number | null } | null>(null);
  const latest = useRef(operation);
  latest.current = operation;
  useEffect(() => {
    if (!signature || !latest.current) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      previewOperation(character.id, latest.current!).then(
        (preview) => {
          const change = preview.changes.find((c) => c.key === key);
          if (!cancelled) setDraft({ signature, number: change ? change.afterNumber : saved });
        },
        () => !cancelled && setDraft(null),
      );
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [character.id, character.revision, signature, key, saved]);
  return signature && draft?.signature === signature ? draft.number : saved;
}

interface Pending {
  operation: Operation;
  title: string;
  preview: PreviewView | null;
  error: ApiError | null;
  settle: (applied: boolean) => void;
}

export function CharacterProvider(props: {
  character: CharacterView;
  system: SystemInfo | undefined;
  language: TermLanguage;
  children: ReactNode;
}) {
  const { character } = props;
  const mutation = useApply(character.id);
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [derivation, setDerivation] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ title: string; changes: ValueChangeView[] } | null>(null);
  const { undo } = useUndoRedo(character.id);

  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const { mutateAsync } = mutation;

  /** Operations run one after another, so each one is sent with the revision its predecessor produced. */
  const run = useCallback(
    (operation: Operation, title: string | null) => {
      const next = queue.current.then(async () => {
        setError(null);
        setNotice(null);
        try {
          const result = await mutateAsync({ operation, reportChanges: title !== null });
          // The log summary of the operation names what was picked; the choice label only names the question.
          if (title !== null) setNotice({ title: result.undoSummary ?? title, changes: result.changes ?? [] });
          return true;
        } catch (e) {
          setError(e instanceof ApiError ? e : new ApiError(0, String(e)));
          return false;
        }
      });
      queue.current = next;
      return next;
    },
    [mutateAsync],
  );
  const apply = useCallback((operation: Operation) => run(operation, null), [run]);
  const choose = useCallback((operation: Operation, title: string) => run(operation, title), [run]);
  const { mutateAsync: undoAsync } = undo;
  const closeNotice = useCallback(() => setNotice(null), []);
  const undoNotice = useCallback(() => {
    setNotice(null);
    queue.current = queue.current.then(() => undoAsync().catch((e: unknown) => setError(e instanceof ApiError ? e : new ApiError(0, String(e)))));
  }, [undoAsync]);

  const confirm = useCallback(
    (operation: Operation, title = "Änderung prüfen") => {
      let settle: (applied: boolean) => void = () => undefined;
      const settled = new Promise<boolean>((resolve) => (settle = resolve));
      setPending((p) => {
        p?.settle(false);
        return { operation, title, preview: null, error: null, settle };
      });
      previewOperation(character.id, operation).then(
        (preview) => setPending((p) => (p && p.operation === operation ? { ...p, preview } : p)),
        (e: unknown) =>
          setPending((p) => (p && p.operation === operation ? { ...p, error: e instanceof ApiError ? e : new ApiError(0, String(e)) } : p)),
      );
      return settled;
    },
    [character.id],
  );

  const system = props.system;
  const value = useMemo<CharacterContextValue>(() => {
    const amount = (currency: string | null | undefined, number: number, signed?: boolean) =>
      formatAmount(character.currencies.find((c) => c.id === currency), number, signed);
    return {
      character,
      system,
      language: props.language,
      value: (key) => character.evaluation.values[key],
      amount,
      display: (computed) => (computed ? (computed.currency && computed.number != null ? amount(computed.currency, computed.number) : (computed.display ?? "")) : ""),
      apply,
      choose,
      confirm,
      showDerivation: setDerivation,
      busy: mutation.isPending,
      error,
      clearError: () => setError(null),
    };
  }, [character, system, props.language, apply, choose, confirm, mutation.isPending, error]);

  return (
    <CharacterContext.Provider value={value}>
      {props.children}
      {pending && (
        <PreviewDialog
          title={pending.title}
          preview={pending.preview}
          error={pending.error}
          busy={mutation.isPending}
          onCancel={() => {
            pending.settle(false);
            setPending(null);
          }}
          onConfirm={async () => {
            if (await apply(pending.operation)) {
              pending.settle(true);
              setPending(null);
            }
          }}
        />
      )}
      {notice && <ChangeNotice title={notice.title} changes={notice.changes} language={props.language} onUndo={undoNotice} onClose={closeNotice} />}
      {derivation && <DerivationSheet valueKey={derivation} onClose={() => setDerivation(null)} onNavigate={setDerivation} />}
    </CharacterContext.Provider>
  );
}
